/**
 * Semantic tone → presentation.
 *
 * Components ask for a *tone*; this module is the only place that decides what a tone looks like.
 * Nothing outside here should write `bg-emerald-50` or `#EF4444` for a semantic purpose: colour
 * literals at each call site drift until green and emerald both mean "good" and amber means
 * "warning" in one file and "ahead of schedule" in the next.
 */

export type Tone =
  | "neutral" // no signal — totals, counts, structural chrome
  | "info" // scheduled, not yet due, in flight and unremarkable
  | "success" // on time, completed cleanly, ahead
  | "warning" // needs watching, approaching a threshold
  | "danger" // late, over capacity, failed
  | "muted" // cancelled, excluded, inert

export interface ToneClasses {
  /** Pill/badge: background + text + border, as one class string. */
  badge: string
  /** Solid dot or bar fill. */
  dot: string
  /** Tinted surface for cards and rows. */
  surface: string
  /** Foreground text on the page background. */
  text: string
  /**
   * Foreground for text sitting on this tone's own `surface`, rather than on the page.
   *
   * `text` is chosen against white, and three of the six tones drop below 4.5:1 once the ground
   * underneath them is their own tint — the red delta on a red card measured 4.17:1. Deepening
   * the ink is the fix rather than paling the tint, because the tint is what makes a card
   * findable at a glance and the ink is what nobody has to see from a distance.
   */
  onSurface: string
  /** Border on a tinted surface. */
  border: string
  /** Hex, for canvas/map/chart contexts that can't take a class. */
  hex: string
}

/**
 * One family per tone, and every one of them drawn from the same palette as the variance bar.
 *
 * A badge on a timeline row and a segment in the band above it describe the same route, so they
 * must not be different reds. These map onto the bar as:
 *
 *   neutral → jungleGreen   (matches `done`)
 *   info    → skyBlue       (matches `on plan`; also the blue a not-started route already wears)
 *   warning → the bar's amber
 *   danger  → the bar's red
 *   success → blueGreen     (the same teal as "ahead", which is what it means)
 *   muted   → jungleGreen, lighter
 *
 * success is teal rather than green on purpose: green next to amber and red is the classic
 * red/green pairing that colour-blind operators cannot separate, and it was already being used
 * for two different ideas ("on time" and "ahead"). Teal is unambiguous against both alarm hues.
 */
const TONES: Record<Tone, ToneClasses> = {
  neutral: {
    badge: "bg-palette-jungleGreen100 text-palette-jungleGreen700 border-palette-jungleGreen200",
    dot: "bg-palette-jungleGreen500",
    surface: "bg-palette-jungleGreen000",
    text: "text-palette-jungleGreen700",
    // Already 8.29:1 on its own tint — a near-white ground needs no deeper ink.
    onSurface: "text-palette-jungleGreen700",
    border: "border-palette-jungleGreen200",
    hex: "#878E90",
  },
  info: {
    badge: "bg-palette-skyBlue100 text-palette-skyBlue800 border-palette-skyBlue300",
    dot: "bg-variance-on-plan",
    surface: "bg-palette-skyBlue000",
    text: "text-palette-skyBlue800",
    onSurface: "text-palette-skyBlue800", // 8.09:1

    border: "border-palette-skyBlue300",
    hex: "#CCE5F3",
  },
  success: {
    badge: "bg-palette-blueGreen100 text-palette-blueGreen800 border-palette-blueGreen200",
    dot: "bg-variance-ahead",
    surface: "bg-palette-blueGreen000",
    text: "text-variance-ahead",
    onSurface: "text-palette-blueGreen800", // 4.53:1 → 7.42:1

    border: "border-palette-blueGreen200",
    hex: "#147A90",
  },
  warning: {
    badge: "bg-variance-at-risk-tint text-palette-honeycomb800 border-variance-at-risk-border",
    dot: "bg-variance-at-risk",
    surface: "bg-variance-at-risk-tint",
    text: "text-palette-honeycomb800",
    onSurface: "text-variance-at-risk-strip", // 4.82:1 → 6.43:1

    border: "border-variance-at-risk-border",
    hex: "#F5A300",
  },
  danger: {
    badge: "bg-variance-late-tint text-variance-late border-variance-late-border",
    dot: "bg-variance-late",
    surface: "bg-variance-late-tint",
    text: "text-variance-late",
    onSurface: "text-variance-late-strip", // 4.17:1 → 9.56:1

    border: "border-variance-late-border",
    hex: "#DC2626",
  },
  muted: {
    badge: "bg-palette-jungleGreen100 text-palette-jungleGreen600 border-palette-jungleGreen200",
    dot: "bg-palette-jungleGreen400",
    surface: "bg-palette-jungleGreen000",
    text: "text-palette-jungleGreen500",
    // Left dim on purpose: muted marks cancelled and excluded rows, and the whole point of the
    // tone is that it recedes. Do not put body text on it.
    onSurface: "text-palette-jungleGreen500",

    border: "border-palette-jungleGreen200",
    hex: "#B7BBBD",
  },
}

export function toneClasses(tone: Tone): ToneClasses {
  return TONES[tone]
}

// ---------------------------------------------------------------------------
// Schedule variance
// ---------------------------------------------------------------------------

/**
 * How far a route has drifted from plan, as an *ordered* scale.
 *
 * This is deliberately a different thing from `Tone`. A tone is categorical — six unrelated
 * meanings that happen to have colours. Variance is one axis with a direction: every step is
 * worse than the last, and the order is the information.
 *
 * That distinction is why the band is readable at all. Four categorical colours cannot be made
 * colour-blind safe (ColorBrewer will only certify a single qualitative four-colour palette),
 * whereas an ordered scale carries its meaning in luminance, which survives every deficiency.
 *
 * The four are strictly monotonic in relative luminance —
 *
 *   normal  0.806 > 0.753 > 0.456 > 0.167
 *   deutan  0.806 > 0.740 > 0.487 > 0.103
 *   protan  0.808 > 0.775 > 0.373 > 0.044
 *   tritan  0.805 > 0.758 > 0.392 > 0.221
 *
 * — so the bar can be read in greyscale, and `on_plan` against `late` never drops below 5.8:1
 * under any simulation. If you change a colour here, re-run that check: keeping the ordering is
 * the whole design, and swapping in a prettier hue can quietly destroy it. Brand ramps are not
 * interchangeable at a given step — skyBlue300 is *lighter* than jungleGreen200, so the obvious
 * pairing of those two inverts `done` and `on_plan` and breaks the scale.
 *
 * There is deliberately no fifth band below `late`. A darker-red step for a route that never
 * departed cannot be told apart from `late`: under protanopia two reds converge no matter how
 * far apart their lightness is set — even pure red against pure black
 * measures 1.26:1, because protanopes already see red as nearly black. No colour fixes that.
 * The distinction is real and worth keeping, so it lives on the cause line, where it is a word
 * ("not departed") rather than a hue — and words survive every deficiency perfectly.
 */
export type Variance = "done" | "on_plan" | "at_risk" | "late"

/**
 * Severity order, ascending: each entry is worse than the one before it.
 *
 * This is the comparison order, not the drawing order — `stabiliseVariance` compares positions in
 * this array to decide whether a route is escalating or improving, so reversing it would silently
 * invert the hysteresis and make bad news the thing that gets held back. Draw with
 * VARIANCE_DISPLAY_ORDER instead.
 */
export const VARIANCE_ORDER: Variance[] = ["done", "on_plan", "at_risk", "late"]

/**
 * Drawing order for the bar and its legend: worst first.
 *
 * The eye lands on the left of a row before anywhere else, so the trouble should be there rather
 * than at the far end past everything that is fine. The scale is still ordered — it simply runs
 * dark to light across the bar instead of light to dark, and a degrading day grows leftward from
 * the edge you are already looking at.
 */
export const VARIANCE_DISPLAY_ORDER: Variance[] = [...VARIANCE_ORDER].reverse()

export interface VarianceClasses {
  /** Solid fill for the bar and legend swatches. */
  fill: string
  /** Deeper fill for the alarm strip, so the announcement is not mistaken for a bar segment. */
  strip?: string
  /** Pale row background, for a timeline row in this state. Undefined where a row stays plain. */
  tint?: string
  /** Hover background for such a row: a deeper shade of `tint`, never the neutral hover grey. */
  tintHover?: string
  /** Left-edge accent for a row in this state. Undefined where a row stays plain. */
  edge?: string
  /** Foreground for text sitting on `fill`. */
  onFill: string
  /** Foreground on the page background. */
  text: string
  /** Hex, for the map and any canvas context that can't take a class. */
  hex: string
}

const VARIANCES: Record<Variance, VarianceClasses> = {
  done: {
    fill: "bg-variance-done",
    onFill: "text-palette-jungleGreen900",
    text: "text-palette-jungleGreen500",
    hex: "#E7E8E9", // jungleGreen100
  },
  on_plan: {
    fill: "bg-variance-on-plan",
    onFill: "text-palette-skyBlue900",
    text: "text-palette-jungleGreen700",
    hex: "#CCE5F3", // skyBlue200
  },
  at_risk: {
    fill: "bg-variance-at-risk",
    strip: "bg-variance-at-risk-strip",
    tint: "bg-variance-at-risk-tint",
    tintHover: "hover:bg-variance-at-risk-tint-hover",
    edge: "border-l-variance-at-risk",
    onFill: "text-palette-jungleGreen900",
    text: "text-palette-honeycomb800",
    hex: "#F5A300",
  },
  late: {
    fill: "bg-variance-late",
    strip: "bg-variance-late-strip",
    tint: "bg-variance-late-tint",
    tintHover: "hover:bg-variance-late-tint-hover",
    edge: "border-l-variance-late",
    onFill: "text-white",
    text: "text-variance-late",
    hex: "#DC2626",
  },
}

export function varianceClasses(variance: Variance): VarianceClasses {
  return VARIANCES[variance]
}

/**
 * Load factor shares one set of thresholds across the stats band, the route details sheet and
 * the planning cost cards. Do not declare a local copy.
 */
export function loadFactorTone(fillPercentage: number): Tone {
  if (fillPercentage >= 100) return "danger"
  if (fillPercentage >= 80) return "warning"
  return "success"
}
